import React, { useCallback, useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { InstalledBaseNode, InstalledBaseNodeTypeKey } from '../codegen/graphql';
import { InstalledBaseNode as InstalledBaseNodeQuery } from '../graphql/queries/installedBaseNode';
import CreateNodeModal from '../components/InstalledBaseTreeColumn/CreateNodeModal';
import { useAllowedChildNodeTypes } from './useAllowedChildNodeTypes';
import { getSubAssetCreationState } from '../utils/subAssetCreation';

export interface SubAssetCreation {
    /** Whether the "Create Sub-Asset" affordance is shown at all. */
    canOffer: boolean;
    /** Shown but not clickable (ASSET child limit reached, or the type is still loading). */
    disabled: boolean;
    /** Why it is disabled, for a tooltip/hint; undefined while merely loading. */
    disabledReason?: string;
    open: () => void;
    /** Render once wherever the hook is used; empty unless opened. */
    modal: React.ReactNode;
    /** The asset's installed-base node (with `pathNodes`), once loaded. */
    node?: InstalledBaseNode;
}

/**
 * "Create Sub-Asset" from the asset dashboard: the tree's create flow (CreateNodeModal
 * straight into the asset registration wizard) with the asset's own node as parent.
 *
 * After creation the user stays on the parent's dashboard; the sub-asset widget, the hero
 * KPI and the parent's child-rule counts are refreshed.
 */
export const useSubAssetCreation = (installedBaseNodeId?: string | null): SubAssetCreation => {
    const { t } = useTranslation();
    const client = useApolloClient();
    const [isOpen, setOpen] = useState(false);
    const open = useCallback(() => setOpen(true), []);

    const { data } = useQuery(InstalledBaseNodeQuery, {
        variables: { id: installedBaseNodeId },
        skip: !installedBaseNodeId,
    });
    const node: InstalledBaseNode | undefined = _.get(data, 'installedBaseNode') ?? undefined;

    const state = getSubAssetCreationState(node);
    // Only query the type catalogue for assets that can actually get sub-assets.
    const { whitelisted } = useAllowedChildNodeTypes(state.canOffer ? node! : (undefined as any));
    const assetType = whitelisted.find((nt) => nt.key === InstalledBaseNodeTypeKey.ASSET);

    const disabledReason = state.limit
        ? t('Asset limit reached ({{current}}/{{max}})', state.limit)
        : undefined;

    const handleCreated = () => {
        client.refetchQueries({
            include: ['InstalledBaseNode', 'InstalledBaseNodeList', 'AssetSubAssetCount'],
        });
    };

    return {
        canOffer: state.canOffer,
        disabled: state.disabled || !assetType,
        disabledReason,
        open,
        node,
        modal: isOpen && node && assetType ? (
            <CreateNodeModal
                open={isOpen}
                onClose={() => setOpen(false)}
                node={node}
                initialNodeType={assetType}
                refetchFunction={handleCreated}
            />
        ) : null,
    };
};
